import React, { useState } from "react";
import {
  Box,
  Button,
  Grid,
  Snackbar,
  Alert,
  TextField,
  MenuItem,
  FormControl,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Formik, Field, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import { AxiosResponse } from "axios";
import { useDispatch } from "react-redux";
import { CmsMasterData } from "types/models/cmsMaster/CmsMasterApp";
import { createCMS } from "redux/actions/cmsMaster";
import RichTextEditor from "@crema/utility/RichTextEditor";

interface CmsProps {
  Cms?: CmsMasterData;
}

const CmsCreate: React.FC<CmsProps> = ({ Cms }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [errorSnackbar, setErrorSnackbar] = useState(false);
  const [successSnackbar, setSuccessSnackbar] = useState(false);

  const validationSchema = Yup.object({
    PageTitle: Yup.string().required("Page URL is required"),
    MetaTitle: Yup.string().required("Page Title is required"),
    MetaKeyword: Yup.string().required("Meta Keyword is required"),
    MetaDescription: Yup.string(),
    Description1: Yup.string(),
    Description2: Yup.string(),
    Description3: Yup.string(),
    Description4: Yup.string(),
    HeaderName1: Yup.string(),
    HeaderName2: Yup.string(),
    HeaderName3: Yup.string(),
    HeaderName4: Yup.string(),
    status: Yup.string().required("Status is required"),
  });

  const handleSubmit = async (values: any) => {
    const dataPayload: CmsMasterData = {
      PageID: BigInt(0).toString(),
      CategoryID: BigInt(0).toString(),
      IsLanding: false,
      Description1: values.Description1,
      Description2: values.Description2,
      Description3: values.Description3,
      Description4: values.Description4,
      IsActive: 1,
      IsHeader: 1,
      MetaTitle: values.MetaTitle,
      MetaKeyword: values.MetaKeyword,
      MetaDesc: values.MetaDescription,
      PageTitle: values.PageTitle,
      HeaderName1: values.HeaderName1,
      HeaderName2: values.HeaderName2,
      HeaderName3: values.HeaderName3,
      HeaderName4: values.HeaderName4,
      status: values.status,
    };

    try {
      console.log("API response: ", dataPayload);
      const response = (await dispatch(
        createCMS(dataPayload)
      )) as unknown as AxiosResponse<any>;
      if (response.status === 200) {
        navigate("/pages");
      }
    } catch (error) {
      console.error("Error creating CMS:", error);
      setErrorSnackbar(true);
    }
  };

  const formatAsBulletPoints = (value: string) => {
    const lines = value.split("\n").filter((line) => line.trim() !== "");
    const formattedText = lines
      .map(
        (line) =>
          `<li style="list-style-position: inside; margin-left: 0;">${line}</li>`
      )
      .join("");
    return `<ul style="padding-left: 0; margin-left: 0; list-style-position: inside; list-style-type: disc;">${formattedText}</ul>`;
  };

  return (
    <Box sx={{ height: "100%", width: "100%", m: 8 }}>
      <Grid
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          height: "100%",
          margin: 2,
        }}
      >
        <Formik
          initialValues={{
            PageTitle: Cms?.PageTitle || "",
            MetaTitle: Cms?.MetaTitle || "",
            MetaKeyword: Cms?.MetaKeyword || "",
            MetaDescription: Cms?.MetaDesc || "",
            Description1: Cms?.Description1 || "",
            Description2: Cms?.Description2 || "",
            Description3: Cms?.Description3 || "",
            Description4: Cms?.Description4 || "",
            HeaderName1: Cms?.HeaderName1 || "",
            HeaderName2: Cms?.HeaderName2 || "",
            HeaderName3: Cms?.HeaderName3 || "",
            HeaderName4: Cms?.HeaderName4 || "",
            status: Cms?.status || "Active",
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ values, errors, touched, isSubmitting, setFieldValue }) => (
            <Form>
              <Grid container spacing={3} mb={3}>
                <Grid item xs={6}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="Page URL"
                    name="PageTitle"
                    variant="outlined"
                    error={touched.PageTitle && !!errors.PageTitle}
                    helperText={<ErrorMessage name="PageTitle" />}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="Page Title"
                    name="MetaTitle"
                    variant="outlined"
                    error={touched.MetaTitle && !!errors.MetaTitle}
                    helperText={<ErrorMessage name="MetaTitle" />}
                  />
                </Grid>
              </Grid>

              <Grid container spacing={3} mb={3}>
                <Grid item xs={6}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="Meta Keyword"
                    name="MetaKeyword"
                    variant="outlined"
                    error={touched.MetaKeyword && !!errors.MetaKeyword}
                    helperText={<ErrorMessage name="MetaKeyword" />}
                  />
                </Grid>

                <Grid item xs={6}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="Status"
                    name="status"
                    select
                    variant="outlined"
                    error={touched.status && !!errors.status}
                    helperText={<ErrorMessage name="status" />}
                  >
                    <MenuItem value="Active">Active</MenuItem>
                    <MenuItem value="InActive">Inactive</MenuItem>
                  </Field>
                </Grid>
              </Grid>

              <Grid container spacing={3} mb={3}>
                <Grid item xs={12}>
                  <Field
                    as={TextField}
                    fullWidth
                    multiline
                    minRows={3}
                    label="Meta Description"
                    name="MetaDescription"
                    variant="outlined"
                    error={touched.MetaDescription && !!errors.MetaDescription}
                    helperText={<ErrorMessage name="MetaDescription" />}
                  />
                </Grid>
              </Grid>

              <Grid container spacing={3} mb={3}>
                {[
                  "HeaderName1",
                  "HeaderName2",
                  "HeaderName3",
                  "HeaderName4",
                ].map((fieldName, idx) => (
                  <Grid item xs={6} key={fieldName}>
                    <FormControl fullWidth>
                      <label>{`Header Name ${idx + 1}`}</label>
                      <div
                        style={{
                          height: 150,
                          overflow: "auto",
                          border: "1px solid #ccc",
                          borderRadius: 4,
                        }}
                      >
                        <RichTextEditor
                          disabled={false}
                          value={values[fieldName]}
                          onChange={(value) => setFieldValue(fieldName, value)}
                        />
                      </div>
                    </FormControl>
                  </Grid>
                ))}
              </Grid>

              <Grid container spacing={3} mb={3}>
                {[
                  "Description1",
                  "Description2",
                  "Description3",
                  "Description4",
                ].map((desc, index) => (
                  <Grid item xs={6} key={desc}>
                    <FormControl fullWidth>
                      <label>{`Description ${index + 1}`}</label>
                      <div
                        style={{
                          height: 150,
                          overflow: "auto",
                          border: "1px solid #ccc",
                          borderRadius: 4,
                        }}
                      >
                        <RichTextEditor
                          disabled={false}
                          value={values[desc]}
                          onChange={(value) =>
                            setFieldValue(desc, formatAsBulletPoints(value))
                          }
                        />
                      </div>
                    </FormControl>
                  </Grid>
                ))}
              </Grid>

              <Box
                sx={{
                  position: "fixed",
                  bottom: 0,
                  left: 0,
                  width: "100%",
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 2,
                  padding: 2,
                  zIndex: 1000,
                  backgroundColor: "white",
                  boxShadow: "0 -2px 10px rgba(0, 0, 0, 0.1)",
                }}
              >
                <Button
                  variant="outlined"
                  color="secondary"
                  type="button"
                  onClick={() => navigate("/pages")}
                  sx={{ textTransform: "none" }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={isSubmitting}
                  sx={{ textTransform: "none" }}
                >
                  {isSubmitting ? "Submitting..." : "Add CMS"}
                </Button>
              </Box>
            </Form>
          )}
        </Formik>
      </Grid>

      <Snackbar
        open={errorSnackbar}
        autoHideDuration={6000}
        onClose={() => setErrorSnackbar(false)}
      >
        <Alert
          onClose={() => setErrorSnackbar(false)}
          severity="error"
          sx={{ width: "100%" }}
        >
          Something went wrong. Please try again!
        </Alert>
      </Snackbar>

      <Snackbar
        open={successSnackbar}
        autoHideDuration={3000}
        onClose={() => setSuccessSnackbar(false)}
      >
        <Alert
          onClose={() => setSuccessSnackbar(false)}
          severity="success"
          sx={{ width: "100%" }}
        >
          CMS Created Successfully!
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CmsCreate;
